import {
  CollectionReference,
  DocumentReference,
  DocumentSnapshot,
} from "@atgof-firebase/types";
import {
  onSceneSheets,
  SceneSheets,
  SheetCategory,
  sheetsCollection,
} from "./sheet";
import { OnNext, onSnapshots } from "./onSnapshot";
import { EpisodePath, perEpisodeRef } from "./episode";
import { notDeleted } from "./general";
import { ReferenceData } from "../common/image";
import {
  kindOfSubject,
  projectRefForSubject,
  subjectCollection,
  SubjectSpec,
} from "./subject";
import { comparePotentiallyNumbered } from "./util";
import { Scene, sortScenes, toScene } from "./scene";

export const APPLICAND_KINDS = ["look", "prop"] as const;
export type ApplicandKind = (typeof APPLICAND_KINDS)[number];

export type UserFacingApplicandKind = ApplicandKind | "item";

export function applicandCollectionName(applicandKind: ApplicandKind) {
  return applicandKind + "s";
}

export function kindOfApplicand(applicandRef: DocumentReference | undefined) {
  const collectionName = applicandRef?.parent.id;
  for (const k of APPLICAND_KINDS) {
    if (collectionName === applicandCollectionName(k)) return k;
  }
  return;
}

export function userFacingApplicandKind(
  applicandKind: ApplicandKind,
  subjectIsCharacter: boolean,
): UserFacingApplicandKind {
  return applicandKind === "prop" && subjectIsCharacter
    ? "item"
    : applicandKind;
}

export function episodePathForApplicand(
  applicandRef: DocumentReference | undefined,
): EpisodePath | undefined {
  const applicandEpRef = applicandRef?.parent.parent;
  if (applicandEpRef?.parent.id === "episodes") {
    const episodeId = applicandEpRef.id;
    const applicandSeasonRef = applicandEpRef.parent.parent;
    const seasonId = applicandSeasonRef?.id;
    const projectRef = applicandSeasonRef?.parent.parent?.parent.parent;
    if (projectRef && seasonId && episodeId) {
      return {
        project: projectRef.id,
        season: seasonId,
        episode: episodeId,
      };
    }
  }
  return;
}

export function subjectRefForApplicand(
  applicandRef: DocumentReference | undefined,
): DocumentReference | undefined {
  const applicandEpRef = applicandRef?.parent.parent;
  if (applicandEpRef?.parent.id === "episodes") {
    return applicandEpRef.parent.parent?.parent.parent ?? undefined;
  }
  return applicandRef?.parent.parent ?? undefined;
}

function projectRefForApplicand(applicandRef: DocumentReference | undefined) {
  return projectRefForSubject(subjectRefForApplicand(applicandRef));
}

function compareApplicands(a: DocumentSnapshot, b: DocumentSnapshot) {
  const [orderA, orderB]: number[] = [a, b].map(
    (snapshot) => snapshot.get("order") ?? -1,
  );
  if (orderA >= 0 && orderB >= 0) return orderA - orderB;
  return comparePotentiallyNumbered(a.get("displayName"), b.get("displayName"));
}

export function onAvailableApplicands(
  applicandKind: ApplicandKind,
  applicandSubjectRef: DocumentReference | undefined,
  episodePaths: EpisodePath[],
  categories: SheetCategory[],
  onNext: OnNext<DocumentSnapshot[] | undefined>,
) {
  if (!(applicandSubjectRef && categories.length)) {
    onNext([]);
    return;
  }
  const collName = applicandCollectionName(applicandKind);
  return onSnapshots<CollectionReference, DocumentSnapshot[]>(
    [
      applicandSubjectRef.collection(collName),
      ...(applicandKind === "look" ? episodePaths : [])
        .filter(({ season, episode }) => season && episode)
        .map((episodePath) =>
          applicandSubjectRef
            .collection("seasons")
            .doc(episodePath.season!)
            .collection("episodes")
            .doc(episodePath.episode!)
            .collection(collName),
        ),
    ],
    (applicandsRef) => (onNext) =>
      notDeleted(applicandsRef)
        .where("category", "in", categories)
        .orderBy("order", "asc")
        .onSnapshot(({ docs }) => onNext(docs.sort(compareApplicands))),
    (collections) => onNext(collections?.flat()),
  );
}

export function applicandReference(
  project: DocumentReference | undefined,
  referenceData: ReferenceData,
) {
  const { kind, applicandId, subjectKind, subjectId, season, episode } =
    referenceData;
  if (!(kind && kind in APPLICAND_KINDS && applicandId && subjectId)) return;
  const applicantKind: ApplicandKind = kind as any;
  const subject = subjectCollection(project, subjectKind)?.doc(subjectId);
  if (!subject) return;
  const parentRef = episode
    ? perEpisodeRef(subject, { season, episode })
    : subject;
  return parentRef
    ?.collection(applicandCollectionName(applicantKind))
    .doc(applicandId);
}

async function scenesReferencingApplicand(applicandRef: DocumentReference) {
  const kind = kindOfApplicand(applicandRef);
  return kind
    ? sortScenes(
        (
          await notDeleted(
            sheetsCollection(projectRefForApplicand(applicandRef)!),
          )
            .where(
              applicandCollectionName(kind),
              "array-contains",
              applicandRef,
            )
            .get()
        ).docs
          .map((doc) => doc.get("scene"))
          .filter((x) => x),
      )
    : [];
}

export async function updateScenesField(applicandRef: DocumentReference) {
  const scenes = await scenesReferencingApplicand(applicandRef);
  await applicandRef.update({ scenes });
}

export function subjectSpecForApplicand(
  applicandRef: DocumentReference | undefined,
): SubjectSpec | undefined {
  const subjectRef = subjectRefForApplicand(applicandRef);
  const subjectKind = kindOfSubject(subjectRef);
  return subjectRef && subjectKind
    ? {
        subjectKind,
        subjectName: subjectRef.id, // TODO This may not work for some subject names due to escaping
      }
    : undefined;
}

export function onApplicandSceneSheets(
  applicand: DocumentSnapshot | undefined,
  onNext: OnNext<SceneSheets[] | undefined>,
) {
  const projectRef = projectRefForApplicand(applicand?.ref);
  const sceneRefs: DocumentReference[] | undefined = applicand?.get("scenes");
  const category: SheetCategory | undefined = applicand?.get("category");
  const subject: SubjectSpec | undefined = subjectSpecForApplicand(
    applicand?.ref,
  );
  return onSnapshots<DocumentReference, Scene>(
    subject ? sceneRefs : undefined,
    (sceneRef) => (onNext) =>
      sceneRef.onSnapshot((doc) => onNext(toScene(doc))),
    (scenes) =>
      onSceneSheets(
        projectRef,
        category ? [category] : [],
        subject,
        scenes,
        onNext,
      ),
  );
}
